// The AI-modification MOCK client: a local, in-memory stand-in for the gateway,
// selected only by REACT_APP_MODIFICATION_MODE=mock. It exists so every screen
// can be walked through on localhost with no network: one record in every
// state for a buyer, a pending queue for a licensor, one locked and one stale
// record for Platform Admin.
//
// It plays the SERVER's part, so it holds the server's kind of data (statuses,
// labels, the limit, the moves): see fixtures.ts. The screens never import it
// directly, and the no-hard-coding test leaves it out on purpose. It mirrors the
// gateway's answers closely, not exactly: the gateway's code is the contract.
import type {
  AgreementDocument,
  AgreementPreview,
  ModificationAcceptance,
  ModificationContractFile,
  ModificationDetail,
  ModificationEvent,
  ModificationRevision,
  ModificationSummary,
} from '../../models/modification.interface';
import { ModificationApiError } from './modificationApi';
import type { ModificationApi } from './modificationApi';
import { MOCK_CONFIG, MOCK_CUSTOMER, MOCK_ORDER_IDS, MOCK_OWNER_NAME, MOCK_SEEDS, MOCK_TENANT, MOCK_TERMS, MOCK_TERMS_WITH_AGREEMENT, MOCK_VIDEO_ID } from './fixtures';
import { MOCK_AGREEMENT_RENDER } from './fixtures.agreement';

type Kind = 'buyer' | 'decider' | 'resetter' | 'admin-viewer' | 'none';

interface MockRecord {
  summary: Omit<ModificationSummary, 'allowedActions' | 'statusLabel' | 'rejectionLimit'>;
  revisions: ModificationRevision[];
  events: ModificationEvent[];
  slot: { revision: number; expiresAt: string; submitted: boolean; lastFailureReason: string | null } | null;
  files: Map<number, File>;
  /** When each link for the current revision was issued, as the gateway keeps it. */
  recentLinks: { revision: number; at: number[] };
  /** Under the addendum: what the buyer gave for the request, and every acceptance. */
  request?: { modificationTypeId: string; instructions: string; production: string };
  acceptances?: ModificationAcceptance[];
}

/** The fixture's real render with this request's values in its filled runs, as the gateway would fill them. */
const fillDocument = (values: Record<string, string>): AgreementDocument => ({
  blocks: MOCK_AGREEMENT_RENDER.preview.document.blocks.map((block) => ({
    type: block.type,
    lines: block.lines.map((line) => line.map((run) => (run.field && values[run.field] !== undefined ? { ...run, text: values[run.field] } : { ...run }))),
  })),
});

const plainTextOf = (document: AgreementDocument): string =>
  document.blocks
    .map((block) => `${block.type === 'bullet' ? '- ' : ''}${block.lines.map((line) => line.map((run) => run.text).join('')).join('\n')}`)
    .join('\n\n');

const BUYERS = ['buyer-user', 'buyer-admin'];
const DAY_MS = 24 * 60 * 60 * 1000;

const fail = (kind: ConstructorParameters<typeof ModificationApiError>[1]['kind'], status: number, serverMessage?: string) =>
  new ModificationApiError(`mock ${status}`, { kind, status, serverMessage });

const kindsFor = (role: string | null): Kind[] => {
  if (role && BUYERS.includes(role)) return ['buyer'];
  if (role === 'lic-approver') return ['decider'];
  if (role === 'lic-admin') return ['decider', 'resetter'];
  if (role === 'cw-platform-admin') return ['resetter', 'admin-viewer'];
  return ['none'];
};

const actionsFor = (record: MockRecord, role: string | null, now: number): string[] => {
  const { status } = record.summary;
  const actions: string[] = [];
  for (const kind of kindsFor(role)) {
    if (kind === 'buyer') {
      if (status === 'awaiting_upload') {
        const live = record.slot && !record.slot.submitted && !record.slot.lastFailureReason && Date.parse(record.slot.expiresAt) > now;
        actions.push(...(live ? ['issue_slot', 'submit'] : ['issue_slot']));
      } else if (status === 'changes_requested' || status === 'rejected') {
        actions.push('next_revision');
      }
    }
    if (kind === 'decider' && status === 'pending') actions.push('accept', 'request_changes', 'reject');
    if (kind === 'resetter' && status === 'locked') actions.push('lock_reset');
  }
  return Array.from(new Set(actions));
};

/** A stand-in hash, so the screen has 64 hex characters to show. Not a real SHA-256. */
const fakeHash = (text: string): string => {
  let out = '';
  let h = 2166136261;
  while (out.length < 64) {
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    out += h.toString(16).padStart(8, '0');
    text += out;
  }
  return out.slice(0, 64);
};

const ACTOR = {
  buyer: { role: 'buyer-user', name: 'Mock Buyer' },
  approver: { role: 'lic-approver', name: 'Mock Approver' },
  admin: { role: 'cw-platform-admin', name: 'Mock Platform Admin' },
  system: { role: 'system', name: 'The platform' },
} as const;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(fail('unknown', 0));
      return;
    }
    if (ms <= 0) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(fail('unknown', 0));
    });
  });

export const createMockModificationApiClient = (
  getRole: () => string | null,
  options: { delayMs?: number; agreement?: boolean } = {}
): ModificationApi => {
  const delayMs = options.delayMs ?? 400;
  // The addendum click-through, as dev has it once the seed runs. Off by default, so the old flow stays testable.
  const agreementOn = options.agreement ?? false;
  const previews = new Map<string, AgreementPreview & { orderId: string; values: Record<string, string>; modificationTypeId: string; consumed: boolean }>();
  const clock = () => Date.now();
  const iso = (ms: number) => new Date(ms).toISOString();
  const records = new Map<string, MockRecord>();
  const replies = new Map<string, unknown>();
  let ids = 0;
  const newId = () => {
    ids += 1;
    return `00000000-0000-4000-8000-${String(ids).padStart(12, '0')}`;
  };

  const addEvent = (record: MockRecord, type: string, actor: keyof typeof ACTOR, to: string, extra: Partial<ModificationEvent> = {}) => {
    const at = iso(clock());
    record.events.push({
      seq: record.events.length + 1,
      type,
      actorRole: ACTOR[actor].role,
      actorDisplayName: ACTOR[actor].name,
      fromStatus: record.summary.status,
      toStatus: to,
      revision: record.summary.currentRevision,
      comment: null,
      reason: null,
      at,
      ...extra,
    });
    record.summary.status = to;
    record.summary.stateVersion += 1;
    record.summary.updatedAt = at;
  };

  const revisionFor = (n: number, at: string): ModificationRevision => ({
    revision: n,
    fileName: `modified-version-${n}.mp4`,
    sizeBytes: 48_000_000,
    contentType: 'video/mp4',
    durationSec: 6,
    width: 1920,
    height: 1080,
    description: 'Mock: colour clean-up of the licensed seconds.',
    category: 'cleanup',
    toolsDeclared: 'Mock AI denoiser',
    submittedAt: at,
    verifiedAt: at,
  });

  // Seed one record per state.
  const start = clock() - 3 * DAY_MS;
  for (const seed of MOCK_SEEDS) {
    const id = newId();
    const created = iso(start);
    const record: MockRecord = {
      summary: {
        modificationId: id,
        orderId: seed.orderId,
        videoId: MOCK_VIDEO_ID,
        title: seed.title,
        status: seed.status,
        currentRevision: seed.revisions,
        rejectionsSinceReset: seed.rejections,
        rejectionCount: seed.rejections,
        orderStatus: 'COMPLETED',
        orderRevoked: false,
        stateVersion: seed.history.length + 1,
        createdAt: created,
        updatedAt: iso(clock() - (seed.pendingDaysAgo ?? 1) * DAY_MS),
        pendingSince: seed.pendingDaysAgo !== undefined ? iso(clock() - seed.pendingDaysAgo * DAY_MS) : null,
        contract:
          seed.status === 'accepted'
            ? { status: 'ready', templateId: 'modification-contract', templateVersion: 1, termsVersion: 1 }
            : { status: 'none', templateId: null, templateVersion: null, termsVersion: null },
        ownerTenant: MOCK_TENANT,
        customerId: MOCK_CUSTOMER,
      },
      revisions: [],
      events: [],
      slot: seed.lastFailureReason ? { revision: seed.revisions, expiresAt: iso(clock() - DAY_MS), submitted: false, lastFailureReason: seed.lastFailureReason } : null,
      files: new Map(),
      recentLinks: { revision: 0, at: [] },
    };
    let revision = 1;
    seed.history.forEach((step, index) => {
      if (step.type === 'slot_issued' && index > 1) revision += 1;
      const actor = ACTOR[step.actor];
      record.events.push({
        seq: index + 1,
        type: step.type,
        actorRole: actor.role,
        actorDisplayName: actor.name,
        fromStatus: index === 0 ? null : seed.history[index - 1].to,
        toStatus: step.to,
        revision: Math.min(revision, seed.revisions),
        comment: step.comment ?? null,
        reason: step.reason ?? null,
        at: iso(start + index * 60 * 60 * 1000),
      });
    });
    for (let n = 1; n <= seed.revisions; n += 1) {
      if (seed.status !== 'awaiting_upload' && seed.status !== 'verifying') record.revisions.push(revisionFor(n, created));
    }
    if (agreementOn) {
      const values: Record<string, string> = { ...MOCK_AGREEMENT_RENDER.preview.values, modificationRequestId: id };
      record.request = { modificationTypeId: MOCK_AGREEMENT_RENDER.preview.modificationTypeId, instructions: values.modificationInstructions, production: values.production };
      record.acceptances = [acceptanceOf('000001', 'request', null, null, fillDocument(values), created)];
    }
    records.set(id, record);
  }

  function acceptanceOf(acceptanceId: string, scope: string, revision: number | null, requestAcceptanceId: string | null, document: AgreementDocument, at: string): ModificationAcceptance {
    const plainText = plainTextOf(document);
    return {
      acceptanceId,
      scope,
      revision,
      requestAcceptanceId,
      acceptedAt: at,
      accountName: ACTOR.buyer.name,
      approvalStatusAtAcceptance: 'approvalRequired',
      agreementTemplateId: MOCK_AGREEMENT_RENDER.agreementTemplate.templateId,
      agreementVersion: MOCK_AGREEMENT_RENDER.agreementTemplate.version,
      renderedSha256: fakeHash(plainText),
      checkboxText: MOCK_AGREEMENT_RENDER.preview.checkboxText,
      buttonLabel: MOCK_AGREEMENT_RENDER.preview.buttonLabel,
      integrity: 'ok',
      document,
      plainText,
    };
  }

  const role = () => getRole();
  const isBuyer = () => BUYERS.includes(role() ?? '');
  const isLicensor = () => role() === 'lic-admin' || role() === 'lic-approver';

  const project = (record: MockRecord): ModificationSummary => {
    const kinds = kindsFor(role());
    const { ownerTenant, customerId, rejectionCount, ...base } = record.summary;
    const view: ModificationSummary = {
      ...base,
      contract: { ...base.contract },
      statusLabel: MOCK_CONFIG.statusLabels[record.summary.status] ?? record.summary.status,
      rejectionLimit: MOCK_CONFIG.rejectionLimit,
      allowedActions: actionsFor(record, role(), clock()),
    };
    if (kinds.includes('buyer')) return { ...view, slot: record.slot ? { ...record.slot } : null };
    // As the server (A6): the owner's name from config beside the tenant id.
    const licensor = { ...view, ownerTenant, ownerName: ownerTenant === MOCK_TENANT ? MOCK_OWNER_NAME : null, rejectionCount };
    return kinds.includes('admin-viewer') ? { ...licensor, customerId } : licensor;
  };

  const detail = (record: MockRecord): ModificationDetail => ({
    ...project(record),
    revisions: record.revisions.map((r) => ({ ...r })),
    events: record.events.map((e) => ({ ...e })),
    licensedRange: { startingTime: '00:00:02', finishingTime: '00:00:08' },
    ...(record.request ? { request: { ...record.request }, acceptances: (record.acceptances ?? []).map((a) => ({ ...a })) } : {}),
  });

  const find = (id: string): MockRecord => {
    const record = records.get(id);
    const kinds = kindsFor(role());
    if (!record || kinds.includes('none')) throw fail('not-found', 404);
    return record;
  };

  const once = async <T>(key: string, run: () => T): Promise<T> => {
    if (replies.has(key)) return replies.get(key) as T;
    const result = run();
    replies.set(key, result);
    return result;
  };

  const settleCheck = (record: MockRecord) => {
    const file = record.files.get(record.summary.currentRevision);
    const failed = Boolean(file && /fail/i.test(file.name));
    if (failed) {
      record.slot = { revision: record.summary.currentRevision, expiresAt: iso(clock()), submitted: false, lastFailureReason: 'not_a_video' };
      addEvent(record, 'verification_failed', 'system', 'awaiting_upload', { reason: 'not_a_video' });
      return;
    }
    const at = iso(clock());
    // Under the addendum the revision keeps the request's type, as the gateway snapshots it.
    const requestType = record.request ? MOCK_AGREEMENT_RENDER.modificationTypes.find((type) => type.id === record.request?.modificationTypeId) : undefined;
    record.revisions.push({
      ...revisionFor(record.summary.currentRevision, at),
      fileName: file?.name ?? `modified-version-${record.summary.currentRevision}.mp4`,
      sizeBytes: file?.size ?? 0,
      ...(requestType ? { category: requestType.id, modificationType: { id: requestType.id, label: requestType.label, description: requestType.description } } : {}),
    });
    record.summary.pendingSince = at;
    addEvent(record, 'verification_passed', 'system', 'pending');
  };

  return {
    async getTerms() {
      await sleep(delayMs);
      // E1 admits the buyer roles and Platform Admin (submission.create).
      if (!isBuyer() && role() !== 'cw-platform-admin') throw fail('forbidden', 403);
      return JSON.parse(JSON.stringify(agreementOn ? MOCK_TERMS_WITH_AGREEMENT : MOCK_TERMS));
    },

    async previewAgreement(body) {
      await sleep(delayMs);
      // E14 is the buyer's alone: the gateway refuses Platform Admin in the service.
      if (!isBuyer()) throw fail('forbidden', 403);
      if (!agreementOn) throw fail('conflict', 409, 'AI modifications are not available for this video right now');
      if (body.orderId !== MOCK_ORDER_IDS.eligible) throw fail('not-found', 404);
      const type = MOCK_AGREEMENT_RENDER.modificationTypes.find((entry) => entry.id === body.modificationTypeId);
      if (!type) throw fail('invalid', 422, 'Invalid category');
      const instructions = body.instructions.trim();
      const production = body.production.replace(/\s+/g, ' ').trim();
      if (!instructions || !production) throw fail('invalid', 422, 'Text is invalid');
      if (instructions.length > MOCK_AGREEMENT_RENDER.limits.instructionsMaxChars || production.length > MOCK_AGREEMENT_RENDER.limits.productionMaxChars) {
        throw fail('invalid', 422, 'Text is too long');
      }
      const modificationId = newId();
      const values = { ...MOCK_AGREEMENT_RENDER.preview.values, selectedModification: type.label, modificationInstructions: instructions, production, modificationRequestId: modificationId };
      const document = fillDocument(values);
      const plainText = plainTextOf(document);
      const preview = {
        previewId: newId(),
        modificationId,
        templateId: MOCK_AGREEMENT_RENDER.agreementTemplate.templateId,
        version: MOCK_AGREEMENT_RENDER.agreementTemplate.version,
        document,
        plainText,
        renderedSha256: fakeHash(plainText),
        checkboxText: MOCK_AGREEMENT_RENDER.preview.checkboxText,
        buttonLabel: MOCK_AGREEMENT_RENDER.preview.buttonLabel,
        approvalStatus: 'approvalRequired',
        expiresAt: iso(clock() + 60 * 60 * 1000),
      };
      previews.set(preview.previewId, { ...preview, orderId: body.orderId, values, modificationTypeId: type.id, consumed: false });
      return JSON.parse(JSON.stringify(preview));
    },

    async getEligibility(orderId) {
      await sleep(delayMs);
      if (!isBuyer()) throw fail('forbidden', 403);
      const existing = Array.from(records.values()).find((r) => r.summary.orderId === orderId);
      if (existing) return { eligible: false, reason: 'limit_reached', modificationId: existing.summary.modificationId };
      if (orderId === MOCK_ORDER_IDS.eligible) return { eligible: true, reason: 'eligible' };
      return { eligible: false, reason: 'usage_not_entitling' };
    },

    async createModification(orderId, key, agreement) {
      await sleep(delayMs);
      if (!isBuyer()) throw fail('forbidden', 403);
      return once(`create:${key}`, () => {
        if (Array.from(records.values()).some((r) => r.summary.orderId === orderId)) throw fail('conflict', 409);
        // Under the addendum the request needs the preview the buyer agreed to: unused, this order's, the same hash.
        const preview = agreement ? previews.get(agreement.previewId) : undefined;
        if (agreementOn && (!agreement || agreement.agreed !== true)) throw fail('invalid', 422, 'The agreement must be accepted first');
        if (agreementOn && (!preview || preview.consumed || preview.orderId !== orderId || preview.renderedSha256 !== agreement?.renderedSha256)) {
          throw fail('conflict', 409, 'This agreement has expired or was already used. Please review it again');
        }
        const id = preview ? preview.modificationId : newId();
        const now = iso(clock());
        const record: MockRecord = {
          summary: {
            modificationId: id,
            orderId,
            videoId: MOCK_VIDEO_ID,
            title: 'Newly licensed clip',
            status: 'awaiting_upload',
            currentRevision: 1,
            rejectionsSinceReset: 0,
            rejectionCount: 0,
            orderStatus: 'COMPLETED',
            orderRevoked: false,
            stateVersion: 1,
            createdAt: now,
            updatedAt: now,
            pendingSince: null,
            contract: { status: 'none', templateId: null, templateVersion: null, termsVersion: null },
            ownerTenant: MOCK_TENANT,
            customerId: MOCK_CUSTOMER,
          },
          revisions: [],
          events: [],
          slot: null,
          files: new Map(),
          recentLinks: { revision: 0, at: [] },
        };
        record.events.push({ seq: 1, type: 'created', actorRole: ACTOR.buyer.role, actorDisplayName: ACTOR.buyer.name, fromStatus: null, toStatus: 'awaiting_upload', revision: 1, comment: null, reason: null, at: now });
        if (preview) {
          preview.consumed = true;
          record.request = { modificationTypeId: preview.modificationTypeId, instructions: preview.values.modificationInstructions, production: preview.values.production };
          record.acceptances = [acceptanceOf('000001', 'request', null, null, preview.document, now)];
          record.events.push({ seq: 2, type: 'agreement_accepted', actorRole: ACTOR.buyer.role, actorDisplayName: ACTOR.buyer.name, fromStatus: 'awaiting_upload', toStatus: 'awaiting_upload', revision: 1, comment: null, reason: 'request', at: now });
        }
        records.set(id, record);
        const view = project(record);
        return { modificationId: id, status: view.status, statusLabel: view.statusLabel, stateVersion: view.stateVersion, allowedActions: view.allowedActions };
      });
    },

    async issueUploadSlot(id, file) {
      await sleep(delayMs);
      if (!isBuyer()) throw fail('forbidden', 403);
      const record = find(id);
      const { status } = record.summary;
      if (status !== 'changes_requested' && status !== 'rejected' && status !== 'awaiting_upload') {
        throw fail('conflict', 409);
      }
      if (!MOCK_TERMS.limits.allowedContentTypes.includes(file.contentType) || file.sizeBytes > MOCK_TERMS.limits.maxBytes) {
        throw fail('invalid', 422, 'The declared file type or size is not allowed.');
      }
      const revision = status === 'awaiting_upload' ? record.summary.currentRevision : record.summary.currentRevision + 1;
      // The rolling window, per revision, as the gateway counts it.
      const windowStart = clock() - MOCK_CONFIG.slotWindowSeconds * 1000;
      const counted = record.recentLinks.revision === revision ? record.recentLinks.at.filter((at) => at > windowStart) : [];
      if (counted.length >= MOCK_CONFIG.maxSlotsPerRevision) {
        throw fail('conflict', 409, MOCK_CONFIG.slotLimitMessage);
      }
      record.recentLinks = { revision, at: [...counted, clock()] };
      record.summary.currentRevision = revision;
      record.slot = { revision: record.summary.currentRevision, expiresAt: iso(clock() + 15 * 60 * 1000), submitted: false, lastFailureReason: null };
      addEvent(record, 'slot_issued', 'buyer', 'awaiting_upload');
      return {
        revision: record.summary.currentRevision,
        uploadUrl: `mock-upload://${id}/rev-${record.summary.currentRevision}`,
        expiresAt: record.slot.expiresAt,
        requiredHeaders: { 'Content-Type': file.contentType },
        stateVersion: record.summary.stateVersion,
      };
    },

    async uploadFile(slot, file, onProgress, signal) {
      // Local only: simulated progress, no request of any kind.
      const steps = 10;
      for (let i = 1; i <= steps; i += 1) {
        await sleep(delayMs / 4, signal);
        onProgress?.(i / steps);
      }
      const match = /^mock-upload:\/\/([^/]+)\/rev-(\d+)$/.exec(slot.uploadUrl);
      const record = match ? records.get(match[1]) : undefined;
      if (record && match) record.files.set(Number(match[2]), file);
    },

    async submitRevision(id, revision, body, key) {
      await sleep(delayMs);
      if (!isBuyer()) throw fail('forbidden', 403);
      return once(`submit:${key}`, () => {
        const record = find(id);
        if (record.summary.status !== 'awaiting_upload' || !record.slot || record.slot.revision !== revision || record.slot.submitted) throw fail('conflict', 409);
        if (record.request) {
          // Under the addendum: the request's type, no typed name, no terms; the request's acceptance recorded again.
          if (body.category !== undefined && body.category !== record.request.modificationTypeId) throw fail('invalid', 422, 'Invalid category');
          const request = (record.acceptances ?? [])[0];
          if (request?.document) {
            const next = String((record.acceptances ?? []).length + 1).padStart(6, '0');
            record.acceptances = [...(record.acceptances ?? []), acceptanceOf(next, 'output', revision, request.acceptanceId, request.document, iso(clock()))];
          }
        } else {
          if (!body.typedName?.trim() || !MOCK_TERMS.limits.categories.includes(body.category ?? '')) throw fail('invalid', 422, 'Please give a typed name and a known category.');
          if (body.acceptedTermsVersion !== MOCK_TERMS.version || body.acceptedTermsTemplateId !== MOCK_TERMS.templateId) {
            throw fail('invalid', 422, 'These terms are no longer current. Please reload the page.');
          }
        }
        record.slot.submitted = true;
        addEvent(record, 'submitted', 'buyer', 'verifying');
        const view = project(record);
        const answer = { status: view.status, statusLabel: view.statusLabel, allowedActions: view.allowedActions, stateVersion: view.stateVersion };
        if (delayMs <= 0) settleCheck(record);
        else setTimeout(() => settleCheck(record), delayMs * 4);
        return answer;
      });
    },

    async listMine(query, signal) {
      await sleep(delayMs, signal);
      if (!isBuyer()) throw fail('forbidden', 403);
      return Array.from(records.values())
        .filter((r) => (!query.status || r.summary.status === query.status) && (!query.orderId || r.summary.orderId === query.orderId))
        .sort((a, b) => b.summary.updatedAt.localeCompare(a.summary.updatedAt))
        .map(project);
    },

    async getModification(id, signal) {
      await sleep(delayMs, signal);
      return detail(find(id));
    },

    async getRevisionFile(id, revision) {
      await sleep(delayMs);
      const record = find(id);
      if (role() === 'cw-platform-admin') throw fail('forbidden', 403);
      const file = record.files.get(revision);
      const url = file && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
      return { url, expiresAt: iso(clock() + 5 * 60 * 1000) };
    },

    async getContract(id, signal): Promise<ModificationContractFile> {
      await sleep(delayMs, signal);
      const record = find(id);
      if (record.summary.status !== 'accepted') throw fail('conflict', 409);
      const text = `%PDF-1.4\n% Mock modification contract for ${id}. DRAFT, not legal text.\n%%EOF\n`;
      record.summary.contract.status = 'ready';
      return { file: new Blob([text], { type: 'application/pdf' }), sha256: fakeHash(text), orderStatus: record.summary.orderStatus };
    },

    async getAcceptancePdf(id, acceptanceId, signal) {
      await sleep(delayMs, signal);
      const record = find(id);
      const acceptance = (record.acceptances ?? []).find((entry) => entry.acceptanceId === acceptanceId);
      if (!acceptance) throw fail('not-found', 404);
      const text = `%PDF-1.4\n% Mock accepted addendum ${acceptanceId} for ${id}. Mock data.\n%%EOF\n`;
      return { file: new Blob([text], { type: 'application/pdf' }), sha256: fakeHash(text), filename: `ai-modification-addendum-${id}-${acceptanceId}.pdf` };
    },

    async listForLicensor(query, signal) {
      await sleep(delayMs, signal);
      if (!isLicensor()) throw fail('forbidden', 403);
      return Array.from(records.values())
        .filter((r) => !query.status || r.summary.status === query.status)
        .sort((a, b) => b.summary.updatedAt.localeCompare(a.summary.updatedAt))
        .map(project);
    },

    async decide(id, body, key) {
      await sleep(delayMs);
      if (!isLicensor()) throw fail('forbidden', 403);
      return once(`decide:${key}`, () => {
        const record = find(id);
        if (body.expectedStateVersion !== record.summary.stateVersion) throw fail('conflict', 409);
        if (record.summary.status !== 'pending' || body.revision !== record.summary.currentRevision) throw fail('conflict', 409);
        const comment = (body.comment ?? '').trim();
        if (!['accept', 'request_changes', 'reject'].includes(body.decision)) throw fail('invalid', 422, 'Unknown decision.');
        if (body.decision !== 'accept' && !comment) throw fail('invalid', 422, 'A comment is required to reject or request changes.');
        if (body.decision === 'accept') {
          record.summary.contract = { status: 'pending', templateId: 'modification-contract', templateVersion: 1, termsVersion: MOCK_TERMS.version };
          addEvent(record, 'accepted', 'approver', 'accepted', { comment: comment || null });
        } else if (body.decision === 'request_changes') {
          addEvent(record, 'changes_requested', 'approver', 'changes_requested', { comment });
        } else {
          record.summary.rejectionsSinceReset += 1;
          record.summary.rejectionCount = (record.summary.rejectionCount ?? 0) + 1;
          const locked = record.summary.rejectionsSinceReset >= MOCK_CONFIG.rejectionLimit;
          addEvent(record, 'rejected', 'approver', locked ? 'locked' : 'rejected', { comment });
        }
        record.summary.pendingSince = null;
        const view = project(record);
        return {
          status: view.status,
          statusLabel: view.statusLabel,
          rejectionsSinceReset: view.rejectionsSinceReset,
          rejectionLimit: view.rejectionLimit,
          stateVersion: view.stateVersion,
          allowedActions: view.allowedActions,
        };
      });
    },

    async resetLock(id, body, key) {
      await sleep(delayMs);
      if (role() !== 'cw-platform-admin' && role() !== 'lic-admin') throw fail('forbidden', 403);
      return once(`reset:${key}`, () => {
        const record = find(id);
        if (body.expectedStateVersion !== record.summary.stateVersion || record.summary.status !== 'locked') throw fail('conflict', 409);
        if (!body.reason.trim()) throw fail('invalid', 422, 'A reason is required.');
        record.summary.rejectionsSinceReset = 0;
        addEvent(record, 'lock_reset', 'admin', 'rejected', { reason: body.reason.trim() });
        const view = project(record);
        return {
          status: view.status,
          statusLabel: view.statusLabel,
          rejectionsSinceReset: view.rejectionsSinceReset,
          rejectionCount: record.summary.rejectionCount ?? 0,
          stateVersion: view.stateVersion,
          allowedActions: view.allowedActions,
        };
      });
    },

    async listForAdmin(query, signal) {
      await sleep(delayMs, signal);
      if (role() !== 'cw-platform-admin' && role() !== 'lic-admin') throw fail('forbidden', 403);
      const cutoff = clock() - MOCK_CONFIG.staleReviewDays * DAY_MS;
      return Array.from(records.values())
        .filter((r) =>
          query.stale
            ? r.summary.status === 'pending' && r.summary.pendingSince !== null && Date.parse(r.summary.pendingSince) < cutoff
            : !query.status || r.summary.status === query.status
        )
        .map(project);
    },
  };
};
